"use client";

import { useCallback, useMemo, useTransition } from "react";
import { useRouter } from "next/navigation";
import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";

import { SfxCategoryPageClientV2, type SfxCategoryPageClientV2Props } from "./SfxCategoryPageClientV2";

type SfxCategoryPageNavigatorProps = Omit<
  SfxCategoryPageClientV2Props,
  "onCategorySelect" | "onCategorySearch" | "isPending"
>;

/** Thin wrapper that injects Next.js router navigation and category search into `SfxCategoryPageClientV2`. */
export function SfxCategoryPageNavigator(props: SfxCategoryPageNavigatorProps) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();

  const handleCategorySelect = useCallback(
    (slug: string) => {
      startTransition(() => {
        router.push(`/sound-effects/${slug}`);
      });
    },
    [router],
  );

  const handleCategorySearch = useCallback(
    async (query: string, signal: AbortSignal) => {
      const params = new URLSearchParams({ q: query, locale: props.locale });
      const res = await fetch(`/next/api/sound-effect-categories/search?${params.toString()}`, {
        signal,
      });
      return res.json();
    },
    [props.locale],
  );

  // Inject onSelect into href-based pills so SearchInput can intercept normal left-clicks
  // and route through startTransition (for the fade). Modifier clicks (cmd, middle-click)
  // fall through to the href and open a new tab as expected.
  const navigableSuggestions = useMemo(
    () =>
      props.searchSuggestions.map(s => {
        if (!s.href) return s;
        const href = s.href;
        return {
          ...s,
          onSelect: (newTab: boolean) => {
            captureEvent(
              EventName.mw_sfx_suggestion_clicked,
              {
                slug: href.split("/").pop() ?? "",
                label: s.label,
              },
              { transport: "sendBeacon" },
            );

            if (!newTab) {
              startTransition(() => router.push(href));
            }
          },
        };
      }),

    [props.searchSuggestions, router],
  );

  return (
    <SfxCategoryPageClientV2
      {...props}
      searchSuggestions={navigableSuggestions}
      isPending={isPending}
      onCategorySelect={handleCategorySelect}
      onCategorySearch={handleCategorySearch}
    />
  );
}
